<app-page-header
        [subtitle]="this.studioId != null ? 'Live update status and history for this studio' : 'Live update status and history across all studios'"
        [title]="this.studioId != null ? 'Firmware Rollout Status - ' + (this.studioSummary?.studioName ?? '') : 'Firmware Rollouts'">
</app-page-header>

@if (this.studioId == null) {
    <app-trigger-firmware-rollout (rolloutTriggered)="refreshAfterTrigger()"></app-trigger-firmware-rollout>
}

<div class="mb-8">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 class="text-xl font-semibold">Active Rollouts</h2>
        @if (this.activeRollouts.length > 1) {
            <p-button
                    (onClick)="this.cancelAllActiveRollouts()"
                    [loading]="this.cancellingAll"
                    icon="pi pi-times"
                    label="Cancel All Active Rollouts"
                    severity="danger"
                    size="small"
                    text>
            </p-button>
        }
    </div>
    @if (!this.loadingActive && this.activeRollouts.length === 0) {
        <p class="text-gray-500 dark:text-gray-400">No firmware rollout is currently active.</p>
    }
    <div class="flex flex-col gap-4">
        @for (rollout of this.activeRollouts; track rollout.id) {
            <div class="border border-gray-300 dark:border-gray-700 rounded-sm p-4">
                <div class="flex flex-wrap items-start justify-between gap-4 mb-3">
                    <div>
                        <p class="text-lg font-semibold">
                            @if (this.studioId == null) {
                                <a [routerLink]="['/studios/studio', rollout.studioId, 'firmware-rollout']">{{ rollout.studioName }}</a>
                                &middot;
                            }
                            Version {{ rollout.targetReleaseVersion }}
                        </p>
                        <p class="text-sm text-gray-500 dark:text-gray-400">
                            Triggered {{ rollout.triggeredAt | formatDateTime }} by {{ rollout.triggeredBy }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <p-tag [severity]="rollout.status === RolloutStatus.PENDING ? 'warn' : 'info'"
                               [value]="rollout.status | enumLabel"></p-tag>
                        <p-button
                                (onClick)="this.cancelRollout(rollout)"
                                [loading]="this.cancellingRolloutId === rollout.id"
                                icon="pi pi-times"
                                label="Cancel"
                                severity="danger"
                                text>
                        </p-button>
                        <p-button
                                [routerLink]="['/firmware-rollouts/rollout', rollout.id]"
                                icon="pi pi-search"
                                label="Details"
                                text>
                        </p-button>
                    </div>
                </div>
                @if (this.phaseLabel(rollout); as phase) {
                    <p [ngClass]="rollout.phase === RolloutPhase.PILOT_FAILED ? 'text-orange-500' : 'text-blue-500 dark:text-blue-300'"
                       class="text-sm font-medium mb-2">
                        {{ phase }}
                    </p>
                }
                <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-2">
                    <span class="text-gray-500 dark:text-gray-400">{{ rollout.sampleCounts.total }} samples</span>
                    @if (rollout.sampleCounts.pending > 0) {
                        <span class="text-gray-500 dark:text-gray-400">{{ rollout.sampleCounts.pending }} pending</span>
                    }
                    @if (rollout.sampleCounts.inProgress > 0) {
                        <span class="text-blue-500 dark:text-blue-300">{{ rollout.sampleCounts.inProgress }}
                            in progress</span>
                    }
                    @if (rollout.sampleCounts.success > 0) {
                        <span class="text-green-500 dark:text-green-300">{{ rollout.sampleCounts.success }}
                            successful</span>
                    }
                    @if (rollout.sampleCounts.error > 0) {
                        <span class="text-red-500 dark:text-red-300">{{ rollout.sampleCounts.error }} failed</span>
                    }
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div
                            [ngClass]="rollout.sampleCounts.error > 0 ? 'bg-orange-500' : 'bg-blue-500'"
                            [style.width.%]="this.progressPercent(rollout)"
                            class="h-full">
                    </div>
                </div>
            </div>
        }
    </div>
</div>

@if (this.studioId != null && this.studioSummary?.failures?.length) {
    <div class="mb-8">
        <h2 class="text-xl font-semibold mb-2">Problem Samples</h2>
        <p-table [value]="this.studioSummary!.failures">
            <ng-template pTemplate="header">
                <tr>
                    <th>MAC Address</th>
                    <th>Error</th>
                    <th>Occurred At</th>
                </tr>
            </ng-template>
            <ng-template let-failure pTemplate="body">
                <tr>
                    <td>{{ failure.macAddress }}</td>
                    <td>{{ failure.errorMessage }}</td>
                    <td>{{ failure.occurredAt | formatDateTime }}</td>
                </tr>
            </ng-template>
        </p-table>
    </div>
}

<div>
    <h2 class="text-xl font-semibold mb-2 text-gray-600 dark:text-gray-400">Rollout History</h2>
    <p-table
            (onLazyLoad)="this.onCompletedPageChange($event)"
            [first]="this.currentPage * this.rows"
            [lazy]="true"
            [loading]="this.loadingCompleted"
            [paginator]="true"
            [rowsPerPageOptions]="[10, 20, 50]"
            [rows]="rows"
            [totalRecords]="this.totalCompleted"
            [value]="this.completedRollouts"
            styleClass="opacity-90"
    >
        <ng-template pTemplate="header">
            <tr>
                <th>Triggered At</th>
                <th>Studio</th>
                <th>Target Version</th>
                <th>Status</th>
                <th>Samples</th>
                <th>Triggered By</th>
                <th></th>
            </tr>
        </ng-template>
        <ng-template let-rollout pTemplate="body">
            <tr>
                <td>{{ rollout.triggeredAt | formatDateTime }}</td>
                <td>{{ rollout.studioName }}</td>
                <td>{{ rollout.targetReleaseVersion }}</td>
                <td>
                    <p-tag
                            [severity]="rollout.status === RolloutStatus.COMPLETED ? 'success'
                                : rollout.status === RolloutStatus.CANCELLED ? 'secondary'
                                : 'danger'"
                            [value]="rollout.status | enumLabel">
                    </p-tag>
                </td>
                <td>
                    {{ rollout.sampleCounts.success }}/{{ rollout.sampleCounts.total }} successful
                    @if (rollout.sampleCounts.error > 0) {
                        <span class="text-red-500 dark:text-red-300">({{ rollout.sampleCounts.error }} failed)</span>
                    }
                </td>
                <td>{{ rollout.triggeredBy }}</td>
                <td>
                    <p-button
                            [routerLink]="['/firmware-rollouts/rollout', rollout.id]"
                            icon="pi pi-search"
                            label="Details"
                            text>
                    </p-button>
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>
